/**
 * `Jun | Jul | Aug 2026` — the one period control in the client portal.
 *
 * `.docs/19-client-portal-pages.md` §DRY watchlist: "`PeriodSeg` is the only period
 * control." Four pages carry one (dashboard, billing, files, the report document)
 * and each of them writes `?period=` and nothing else, because
 * `ARCHITECTURE.md` §Frontend says the URL is the state and the back button has to
 * work.
 *
 * Two decisions worth stating:
 *
 *   - **Only the last button carries the year.** `mockups/client/dashboard.html`
 *     draws `Jun · Jul · Aug 2026`, and repeating "2026" three times is the chrome
 *     DESIGN.md P7 deletes. The accessible name is the full month and year on every
 *     one of them, so a screen reader is not asked to infer it.
 *   - **It writes the period, it does not hold it.** The parent reads
 *     `useSearchState` (or its route's `validateSearch`) and passes `value` back in,
 *     so a page that arrives at `?period=2026-06` opens on June with no effect to
 *     synchronise.
 */

import { previousPeriod, parsePeriod } from '@fareast/shared'

import { Seg, SegItem } from '../portal/Button.js'
import { useFormat } from '../../format/useFormat.js'

/** How many months the control offers. The mockups all draw three. */
export const PERIOD_SEG_MONTHS = 3

export type PeriodSegProps = {
  /** The month that is on. A `YYYY-MM`. */
  readonly value: string
  /**
   * The newest month the control offers, which is normally the current one. The
   * rest are counted back from it, so a page looking at June still offers July and
   * August.
   */
  readonly latest: string
  readonly onChange: (period: string) => void
  /** How many buttons. Three, unless a page has a reason. */
  readonly months?: number
  /** What the group is called. "Month" unless the page means something narrower. */
  readonly label?: string
}

/**
 * The months a control offers, newest last — `['2026-06', '2026-07', '2026-08']`.
 *
 * Exported because the report archive lists the same months as links rather than as
 * buttons, and two ways of counting back three months is one too many.
 */
export function periodsEndingAt(latest: string, months = PERIOD_SEG_MONTHS): readonly string[] {
  const found: string[] = []
  let cursor = parsePeriod(latest)
  for (let step = 0; step < Math.max(1, months); step += 1) {
    found.unshift(cursor)
    cursor = previousPeriod(cursor)
  }
  return found
}

export function PeriodSeg({ value, latest, onChange, months, label }: PeriodSegProps) {
  const format = useFormat()
  const periods = periodsEndingAt(latest, months ?? PERIOD_SEG_MONTHS)

  return (
    <Seg label={label ?? 'Month'}>
      {periods.map((period, index) => {
        const full = format.month(period)
        const last = index === periods.length - 1
        return (
          <SegItem
            key={period}
            on={period === value}
            onClick={() => {
              onChange(period)
            }}
            aria-label={full}
          >
            {last ? full : full.split(' ')[0]}
          </SegItem>
        )
      })}
    </Seg>
  )
}
